import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Save, Plus, Edit, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const DEFAULT_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$', rate: 1.0 },
  { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.92 },
  { code: 'GBP', name: 'British Pound', symbol: '£', rate: 0.79 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', rate: 149.50 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', rate: 1.52 }
];

const CurrencyRatesTab = () => {
  const [currencies, setCurrencies] = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [currencyForm, setCurrencyForm] = useState({
    code: '',
    name: '',
    symbol: '',
    rate: ''
  });

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await api.get('/system-settings/currencies');
      if (response.data && response.data.length > 0) {
        setCurrencies(response.data);
      } else {
        // Initialize with defaults
        setCurrencies(DEFAULT_CURRENCIES);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setCurrencies(DEFAULT_CURRENCIES);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await api.post('/system-settings/currencies', { currencies });
      toast.success('Currency rates saved');
    } catch (error) {
      console.error('Error saving currencies:', error);
      toast.error('Failed to save currency rates');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRate = (index, newRate) => {
    const updated = [...currencies];
    updated[index].rate = parseFloat(newRate) || 0;
    setCurrencies(updated);
  };

  const handleAddCurrency = () => {
    setEditingCurrency(null);
    setCurrencyForm({ code: '', name: '', symbol: '', rate: '' });
    setDialogOpen(true);
  };

  const handleEditCurrency = (currency, index) => {
    setEditingCurrency(index);
    setCurrencyForm({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      rate: currency.rate.toString()
    });
    setDialogOpen(true);
  };

  const handleSaveCurrency = () => {
    if (!currencyForm.code || !currencyForm.name || !currencyForm.rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newCurrency = {
      code: currencyForm.code.toUpperCase(),
      name: currencyForm.name,
      symbol: currencyForm.symbol,
      rate: parseFloat(currencyForm.rate)
    };

    if (editingCurrency !== null) {
      const updated = [...currencies];
      updated[editingCurrency] = newCurrency;
      setCurrencies(updated);
    } else {
      setCurrencies([...currencies, newCurrency]);
    }

    setDialogOpen(false);
    toast.success('Currency added - remember to save');
  };

  const handleDeleteCurrency = async (index) => {
    if (!await confirm({ title: 'Delete this currency?', destructive: true })) return;
    const updated = currencies.filter((_, i) => i !== index);
    setCurrencies(updated);
    toast.success('Currency removed - remember to save');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Currency Exchange Rates</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              All rates are relative to 1 USD
            </p>
          </div>
          <Button onClick={handleAddCurrency}>
            <Plus className="w-4 h-4 mr-2" /> Add Currency
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            {currencies.map((currency, index) => (
              <div key={currency.code} className="flex items-center gap-4 p-4 border rounded">
                <div className="w-20">
                  <p className="font-bold text-lg">{currency.code}</p>
                  <p className="text-xs text-muted-foreground">{currency.symbol}</p>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{currency.name}</p>
                </div>
                <div className="w-48 flex items-center gap-2">
                  <Label className="text-xs">Rate:</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={currency.rate}
                    onChange={(e) => handleUpdateRate(index, e.target.value)}
                    className="text-right"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEditCurrency(currency, index)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  {currency.code !== 'USD' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteCurrency(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t">
            <Button onClick={handleSaveAll} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save All Rates</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Currency Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCurrency !== null ? 'Edit' : 'Add'} Currency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Currency Code *</Label>
              <Input
                value={currencyForm.code}
                onChange={(e) => setCurrencyForm({ ...currencyForm, code: e.target.value })}
                placeholder="e.g., CAD"
                maxLength={3}
                disabled={editingCurrency !== null}
              />
              <p className="text-xs text-muted-foreground">3-letter ISO code</p>
            </div>

            <div className="space-y-2">
              <Label>Currency Name *</Label>
              <Input
                value={currencyForm.name}
                onChange={(e) => setCurrencyForm({ ...currencyForm, name: e.target.value })}
                placeholder="e.g., Canadian Dollar"
              />
            </div>

            <div className="space-y-2">
              <Label>Symbol</Label>
              <Input
                value={currencyForm.symbol}
                onChange={(e) => setCurrencyForm({ ...currencyForm, symbol: e.target.value })}
                placeholder="e.g., C$"
              />
            </div>

            <div className="space-y-2">
              <Label>Exchange Rate (to 1 USD) *</Label>
              <Input
                type="number"
                step="0.0001"
                value={currencyForm.rate}
                onChange={(e) => setCurrencyForm({ ...currencyForm, rate: e.target.value })}
                placeholder="e.g., 1.35"
              />
              <p className="text-xs text-muted-foreground">
                How many units of this currency = 1 USD
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCurrency}>Save Currency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrencyRatesTab;
